import useGetMe from '@/api/queries/me/useGetMe';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import ProposalDepositModal from '@/ui/pages/proposalBuilderPage/components/ProposalDepositModal';
import { useHashFragment } from '@/utils/customHooks';
import { Button } from '@payaca/ambrosia';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';

const DEPOSIT_MODAL_HASH = '#set-proposal-deposit';

export type TProps = {
  proposalId: string;
};

/**
 * Opens the deposit modal to set or clear the deposit requested on a
 * Proposal.
 */
const RequestDepositButton: FC<TProps> = (props) => {
  const { proposalId } = props;

  const [isDepositModalOpen, toggleDepositModal] =
    useHashFragment(DEPOSIT_MODAL_HASH);

  // Keys the modal per open so its form remounts with a fresh session,
  // discarding cancelled edits. Derived during render (not in the toggle)
  // because the hash can open the modal without the toggle running, e.g.
  // browser Back/Forward. Only bumped on open, so the close animation's
  // instance stays mounted.
  const [openCount, setOpenCount] = useState(0);
  const [wasOpen, setWasOpen] = useState(isDepositModalOpen);
  if (isDepositModalOpen !== wasOpen) {
    setWasOpen(isDepositModalOpen);
    if (isDepositModalOpen) {
      setOpenCount((count) => count + 1);
    }
  }

  const { proposal } = useGetProposal(proposalId);
  const { permissions } = useGetMe();

  // Read the raw configured deposit, not effectivePaymentSchedule — the
  // effective stages are grant-adjusted, so seeding from them would show
  // (and re-save) the grant-reduced figure rather than what was entered.
  const depositAmount = proposal?.paymentSchedule
    ? null
    : (proposal?.depositAmount ?? null);
  const depositPercentage = proposal?.paymentSchedule
    ? null
    : (proposal?.depositPercentage ?? null);
  const hasDeposit = depositAmount != null || depositPercentage != null;

  const isDisabled =
    !proposal || !permissions?.includes(JobPermissions.EDIT_JOB_DEPOSIT);

  return (
    <>
      <Button
        variant="tertiary"
        className="w-full"
        disabled={isDisabled}
        onClick={toggleDepositModal}
      >
        <UntitledIcon
          name={hasDeposit ? 'edit-01.3' : 'plus.3'}
          className="w-4 h-4"
        />
        {hasDeposit ? 'Update deposit' : 'Request deposit'}
      </Button>
      <ProposalDepositModal
        key={openCount}
        isOpen={isDepositModalOpen}
        onClose={toggleDepositModal}
        proposalId={proposalId}
        title={hasDeposit ? 'Update deposit' : 'Add deposit'}
        currentDepositAmount={depositAmount}
        currentDepositPercentage={depositPercentage}
      />
    </>
  );
};

export default RequestDepositButton;
